import Environment from '../../environment';
import axios from 'axios';
import {onFulfilled} from './interceptors/request';
import {handleLogout} from '@/Utils/CommonUtils';
import {useNavigation} from '@react-navigation/native';
import {ScreenName} from '@/navigators/StackConstants';

const useChatRestClient = () => {
  const navigation = useNavigation<any>();

  const ChatRestClient = axios.create({
    baseURL: Environment.CHAT_API_URL,
  });

  ChatRestClient.interceptors.request.use(onFulfilled);
  // Attaches the WAF token as an explicit header rather than relying on the
  // aws-waf-token cookie (which would need withCredentials + the backend to
  // allow credentialed CORS from every calling origin -- see AWS's guidance
  // on cross-domain fetch: https://docs.aws.amazon.com/waf/latest/developerguide/waf-js-challenge-api.html).
  // Scoped to send_otp only: that's the first WAF-gated call in the OTP
  // flow. Guarded and fails open (request proceeds untouched) since the SDK
  // script isn't loaded in every environment (e.g. local dev) and getToken()
  // can time out.
  ChatRestClient.interceptors.request.use(async config => {
    if (config.url?.includes('send_otp')) {
      try {
        const token = await window.AwsWafIntegration?.getToken();
        if (token) {
          config.headers['x-aws-waf-token'] = token;
        }
      } catch (e) {
        console.warn('AwsWafIntegration.getToken() failed', e);
      }
    }
    return config;
  });

  ChatRestClient.interceptors.response.use(
    response => {
      // If the request was made and the server responded with a status code
      // that falls out of the range of 2xx, this function will not be called.
      return response;
    },
    error => {
      // The OTP endpoints are the pre-login calls, and a 403 on them is the
      // WAF block action (rate limit), not a dead session -- signing the user
      // out and bouncing them to Login there both loses the error message and
      // logs out anyone who happens to be signed in already.
      const isOtpEndpoint = /auth\/v1\/(send|verify)_otp/.test(
        error?.config?.url || '',
      );
      // If request was sent and server responded with a status outside of the 2xx range
      if (error.response && !isOtpEndpoint) {
        switch (error.response.status) {
          case 401:
            // Handle 401 error code.
            handleLogout(() => {
              navigation?.navigate(ScreenName?.Login);
            });
            console.error('401 Unauthorized Error');
            break;
          case 403:
            // Handle 403 error code.
            handleLogout(() => {
              navigation?.navigate(ScreenName?.Login);
            });
            console.error('403 Forbidden Error');
            break;
          default:
            break;
        }
      }
      // A request that never got a response (network drop, CORS, timeout) has
      // no `error.response`; reading `.data` off it threw a TypeError inside
      // the interceptor, so the caller's onError saw that instead of the
      // failure. Keep the status alongside the body so callers can tell a
      // rate-limit block from an app-level error.
      if (!error.response) {
        return Promise.reject(error);
      }
      const body = error.response.data;
      return Promise.reject(
        body && typeof body === 'object'
          ? {...body, status: error.response.status}
          : {status: error.response.status, error: body},
      );
    },
  );
  return ChatRestClient;
};
export default useChatRestClient;
